import React from 'react';
import Icon from 'components/styleguide/icon/Icon';
import Tag from 'components/styleguide/tag/Tag';
import { displayPct, EffectiveScores } from './utils/curationScoring';
import { useCurationReview } from './state/CurationReviewContext';

const styles = require('./OverallScore.module.scss');

export interface Props {
  scores: EffectiveScores;
}

const BAR_MAX = 100;

// The AI match score card: one large percentage, the category pill, and the
// two weighted dimension bars.
export default function OverallScore({ scores }: Props) {
  const { initialReview } = useCurationReview();
  const analysedAt = initialReview.ai.completedAt;

  const bar = (label: string, value: number | null) => (
    <div className={styles['ui-overall-score__row']}>
      <span className={styles['ui-overall-score__row-label']}>{label}</span>
      <span aria-hidden className={styles['ui-overall-score__track']}>
        <span
          className={styles['ui-overall-score__bar']}
          style={{ transform: `scaleX(${(value || 0) / BAR_MAX})` }}
        />
      </span>
      <span className={styles['ui-overall-score__row-value']}>{displayPct(value)}</span>
    </div>
  );

  return (
    <div className={styles['ui-overall-score']}>
      <div className={styles['ui-overall-score__head']}>
        <div className={styles['ui-overall-score__score']}>
          <span className={styles['ui-overall-score__caption']}>AI match score</span>
          <div className={styles['ui-overall-score__value-row']}>
            <span className={styles['ui-overall-score__number']}>{displayPct(scores.overall)}</span>
            {scores.category === 'qualified' && (
              <Tag iconLeft="circleCheck" variant="success">
                Qualified candidate
              </Tag>
            )}
            {scores.category === 'probably_not_a_match' && (
              <Tag iconLeft="circleAlert" variant="warning">
                Probably not a match
              </Tag>
            )}
          </div>
        </div>
        <div className={styles['ui-overall-score__meta']}>
          <span className={styles['ui-overall-score__ai-chip']}>
            <Icon aria-hidden name="sparkles" size="sm" />
            AI generated
          </span>
          <span>From the application CV</span>
          {analysedAt && <span>Analysed {new Date(analysedAt).toLocaleString()}</span>}
        </div>
      </div>

      <div className={styles['ui-overall-score__bars']}>
        {bar('Skills', scores.skills)}
        {bar('Experience', scores.experience)}
      </div>
    </div>
  );
}
